import { api, resetAssistant, useGetTeam, useGetUser } from '@agently/api';
import { Broker, User } from '@agently/types';
import { setUser } from '@agently/utils';
import { useStore } from '@nanostores/react';
import { map } from 'nanostores';
import { persistentAtom } from '@nanostores/persistent';
import * as Sentry from '@sentry/react';
import routes from 'routes';

import { clearQueryCache, queryClient } from 'providers/QueryProvider';
import {
  clearCachedBrandbook,
  ensureBrandbookOwner,
  setCachedBrandbook,
} from 'stores/brandbook';

const $user = persistentAtom<null | User>('user', null, {
  encode: JSON.stringify,
  decode: JSON.parse,
});

const AUTH_STARTED_MARK = 'auth_started';
const AUTH_PROFILE_READY_MARK = 'auth_profile_ready';

const markAuthProfileReady = (authStartedAt: number) => {
  performance.mark(AUTH_PROFILE_READY_MARK);
  performance.measure(
    'auth_profile_duration',
    AUTH_STARTED_MARK,
    AUTH_PROFILE_READY_MARK,
  );

  const profileEntries = performance
    .getEntriesByType('resource')
    .filter(
      (entry): entry is PerformanceResourceTiming =>
        entry.entryType === 'resource' &&
        entry.name.includes('/agent/profile') &&
        entry.startTime >= authStartedAt,
    );
  const profileEntry = profileEntries[profileEntries.length - 1];

  Sentry.addBreadcrumb({
    category: 'mobile.startup',
    data: {
      authProfileDuration: performance.now() - authStartedAt,
      serviceWorkerQueueDuration:
        profileEntry && profileEntry.workerStart > 0
          ? profileEntry.workerStart - profileEntry.startTime
          : null,
    },
    message: AUTH_PROFILE_READY_MARK,
  });
};

export const $authState = map<{
  isAuth: 'false' | 'true';
  isLoaded: 'false' | 'true';
}>({
  isAuth: 'false',
  isLoaded: 'false',
});

const getInvite = async () => {
  const { pathname, search } = location;
  const query = new URLSearchParams(search);
  const inviteId = query.get('inviteId') as string;
  if (!inviteId || inviteId === 'null') return Promise.resolve();
  try {
    const { data } = await api.get<{
      agent: {
        joined: boolean;
        user: {
          email: null | string;
          name: null | string;
          signupState: string;
        };
      };
      broker: Broker;
    }>(`/invitations/${inviteId}`);
    const { agent } = data;
    if (!agent.joined && !pathname.includes(routes.signupPath)) {
      location.replace(
        `${routes.signupPath}?step=create-account&inviteId=${inviteId}`,
      );
      return Promise.resolve();
    }
    if (agent.joined && pathname.includes(routes.signupPath)) {
      location.replace(routes.todayPath);
    }
  } catch {
    return Promise.resolve();
  }
  return Promise.resolve();
};

export const fetchUser = async () => {
  const cachedUser = $user.get();
  if (cachedUser) {
    void queryClient
      .fetchQuery({
        ...useGetUser.getFetchOptions(),
        staleTime: 0,
      })
      .then((data) => {
        ensureBrandbookOwner(data.id);
        $user.set(data);
      })
      .catch(() => {
        logout();
      });
    return cachedUser;
  }
  try {
    const data = await queryClient.fetchQuery(useGetUser.getFetchOptions());
    $user.set(data);
    return data;
  } catch {
    logout();
  }
};

const logout = () => {
  setUser(null);
  localStorage.removeItem('authToken');
  $authState.setKey('isAuth', 'false');
  $user.set(null);
  clearCachedBrandbook();
  resetAssistant();
  // Clear query cache - errors are handled internally
  clearQueryCache().catch(() => {
    // Ignore errors - cache clearing is best effort
  });
};

const authUser = async () => {
  const authStartedAt = performance.now();
  performance.mark(AUTH_STARTED_MARK);
  await getInvite();

  try {
    const user = await fetchUser();
    if (!user) {
      logout();
      return;
    }
    markAuthProfileReady(authStartedAt);
    ensureBrandbookOwner(user.id);
    setUser({
      email: user.email,
      id: user.id.toString(),
      username: user.name,
    });

    $authState.setKey('isAuth', 'true');
    $authState.setKey('isLoaded', 'true');

    void queryClient
      .fetchQuery({
        ...useGetTeam.getFetchOptions(),
        staleTime: 0,
      })
      .then((team) => setCachedBrandbook(user.id, team.brandbook))
      .catch((error) => {
        console.error('Failed to fetch team or update brandbook:', error);
      });

    return user;
  } catch {
    logout();
    await getInvite();
  } finally {
    $authState.setKey('isLoaded', 'true');
  }
};

const init = async () => {
  // Auth logic - check if user has token and initialize auth state
  if (localStorage.getItem('authToken')) {
    await authUser();
  } else {
    logout();
    $authState.setKey('isLoaded', 'true');
    await getInvite();
  }
};

const useUser = () => useStore($user)!;

const useAuthState = () => useStore($authState);

export { authUser, init, logout, useAuthState, useUser };
